/**
 * PlacePicker – a compact combobox plus LLM-suggestion chip for task and
 * action-item forms.
 *
 * Usage:
 *   <PlacePicker
 *     value={form.place_id}
 *     onChange={(id) => setForm(f => ({ ...f, place_id: id }))}
 *     suggestFromText={`${form.title} ${form.description ?? ''}`}
 *   />
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapPin, Sparkles, X } from 'lucide-react'
import {
  placeService, PLACE_AUTO_ATTACH_THRESHOLD as AUTO_ATTACH_THRESHOLD,
  type Place, type PlaceSuggestion,
} from '../../services/placeService'
import { BRAND } from '../../config/brand'

export interface PlacePickerProps {
  value: number | null | undefined
  onChange: (placeId: number | null) => void
  /** Free-text that should drive LLM suggestions — usually title + description. */
  suggestFromText?: string
  disabled?: boolean
  placeholder?: string
  className?: string
}

function useDebounced<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const h = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(h)
  }, [value, delay])
  return debounced
}

export function PlacePicker({
  value,
  onChange,
  suggestFromText,
  disabled,
  placeholder = 'No location',
  className,
}: PlacePickerProps) {
  const [places, setPlaces] = useState<Place[]>([])
  const [suggestion, setSuggestion] = useState<PlaceSuggestion | null>(null)
  const [suggestionDismissed, setSuggestionDismissed] = useState(false)
  const [autoAttached, setAutoAttached] = useState<number | null>(null)

  const mountedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    void placeService
      .list()
      .then((list) => {
        if (!cancelled) setPlaces(list)
      })
      .catch(() => {
        if (!cancelled) setPlaces([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const debouncedText = useDebounced(suggestFromText ?? '', 500)

  useEffect(() => {
    // Only ask for suggestions when the user hasn't already picked a place
    // or explicitly dismissed one.
    if (value || suggestionDismissed) {
      setSuggestion(null)
      return
    }
    const text = debouncedText.trim()
    if (text.length < 3) {
      setSuggestion(null)
      return
    }
    let cancelled = false
    void placeService
      .suggestForTask(text, 3)
      .then((candidates) => {
        if (cancelled) return
        const top = candidates[0] ?? null
        setSuggestion(top)
        // Only auto-attach once per mount and only if we haven't already
        // swapped in a different place by hand.
        if (
          top &&
          top.confidence >= AUTO_ATTACH_THRESHOLD &&
          !mountedRef.current &&
          !value
        ) {
          onChange(top.place_id)
          setAutoAttached(top.place_id)
        }
        mountedRef.current = true
      })
      .catch(() => {
        if (!cancelled) setSuggestion(null)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedText, value, suggestionDismissed, onChange])

  const current = useMemo(
    () => (value ? places.find((p) => p.id === value) ?? null : null),
    [places, value],
  )

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const raw = e.target.value
      const id = raw === '' ? null : parseInt(raw, 10)
      onChange(Number.isFinite(id) ? (id as number) : null)
      setSuggestionDismissed(true)
      setAutoAttached(null)
    },
    [onChange],
  )

  const handleClear = useCallback(() => {
    onChange(null)
    setSuggestionDismissed(true)
    setAutoAttached(null)
  }, [onChange])

  const handleAcceptSuggestion = useCallback(() => {
    if (!suggestion) return
    onChange(suggestion.place_id)
  }, [suggestion, onChange])

  const handleDismissSuggestion = useCallback(() => {
    setSuggestion(null)
    setSuggestionDismissed(true)
    if (autoAttached && value === autoAttached) {
      onChange(null)
      setAutoAttached(null)
    }
  }, [autoAttached, value, onChange])

  const showSuggestionChip =
    suggestion &&
    !suggestionDismissed &&
    (!value || value === suggestion.place_id) &&
    (value !== suggestion.place_id || autoAttached === suggestion.place_id)

  return (
    <div className={className}>
      <label className="flex items-center gap-2 text-sm font-medium text-primary">
        <MapPin className="h-4 w-4 text-brand" />
        Place
      </label>

      <div className="mt-1 flex items-stretch gap-2">
        <select
          value={value ?? ''}
          onChange={handleSelect}
          disabled={disabled}
          className="flex-1 rounded-md border border-soft bg-surface text-primary px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:opacity-50"
        >
          <option value="">{placeholder}</option>
          {places.map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
              {p.address ? ` — ${p.address}` : ''}
            </option>
          ))}
        </select>
        {value != null && (
          <button
            type="button"
            onClick={handleClear}
            className="rounded-md border border-soft bg-surface px-2 text-secondary hover:text-primary"
            aria-label="Clear place"
            title="Clear place"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {current && (
        <div className="mt-1 text-xs text-tertiary">
          Trigger radius: {current.radius_m}m
        </div>
      )}

      {showSuggestionChip && suggestion && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-brand/40 bg-brand-subtle/50 px-2.5 py-1.5 text-sm">
          <Sparkles className="h-4 w-4 text-brand flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {autoAttached === suggestion.place_id ? (
              <span className="text-primary truncate">
                Attached <strong>{suggestion.title}</strong> ({BRAND.assistant} thinks this is where
                you'll do it).
              </span>
            ) : (
              <span className="text-primary truncate">
                {BRAND.assistant} thinks: <strong>{suggestion.title}</strong>
                {suggestion.address ? ` — ${suggestion.address}` : ''}
              </span>
            )}
          </div>
          {autoAttached !== suggestion.place_id && value !== suggestion.place_id && (
            <button
              type="button"
              onClick={handleAcceptSuggestion}
              className="text-xs font-semibold text-brand hover:underline"
            >
              attach
            </button>
          )}
          <button
            type="button"
            onClick={handleDismissSuggestion}
            className="text-tertiary hover:text-primary"
            aria-label="Dismiss suggestion"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}

export default PlacePicker
